import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { ConnectionData, ConnectionNoteData, ConnectionStatus } from 'src/lib/fliko/models/connection/connection.data';
import { Collection } from 'src/lib/fliko/types/database.type';
import { NulCol } from 'src/app/shared/services/database/dbCollection';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { ConnectionsService } from './connections.service';
import { ResidentConnectionNoteVM, ResidentConnectionVM } from './models/connections.view-model';

@Injectable({ providedIn: 'root' })
export class ConnectionsDataService {
  constructor(
    private connectionsService: ConnectionsService,
    private ctxDS: ContextDataService,
    private ls: LocalizationService
  ) {}

  public getConnections$(): Observable<ResidentConnectionVM[]> {
    return combineLatest([
      this.connectionsService.getConnectionHistory$(),
      this.connectionsService.getConnectionNotes$(),
      this.ctxDS.currentEstate$,
      this.ls.onLanguageChanged$
    ]).pipe(
      map(([connections, notesByConnection, estate]) => {
        if (!estate?.id) return [];

        return connections
          .filter((connection) => connection.estateId === estate.id)
          .map((connection) => this.connectionToVM(connection, notesByConnection));
      })
    );
  }

  private connectionToVM(
    connection: ConnectionData,
    notesByConnection: NulCol<Collection<ConnectionNoteData>>
  ): ResidentConnectionVM {
    return {
      id: connection.id,
      residentId: connection.residentId ?? null,
      residentName: connection.residentName?.trim() || this.ls.translate('connection.unassignedNumber'),
      residentPhone: connection.residentPhone,
      adminPhone: connection.adminNumber,
      line: connection.line,
      direction: connection.direction,
      status: connection.status,
      createdAt: connection.createdAt,
      endedAt: connection.endedAt ?? null,
      durationSec: connection.durationSec ?? null,
      isOngoing: connection.status === ConnectionStatus.ONGOING,
      notes: this.notesToVM(notesByConnection?.[connection.id])
    };
  }

  private notesToVM(notes: Collection<ConnectionNoteData> | null | undefined): ResidentConnectionNoteVM[] {
    return ObjHlp.flatten<ConnectionNoteData>(notes, 1)
      .sort((a, b) => b.createdAt - a.createdAt)
      .map((note) => ({
        id: note.id,
        author: note.authorName,
        content: note.content,
        createdAt: note.createdAt
      }));
  }
}
